import {Presentation, PresentationState} from "@nolimitcity/slot-game/bin/core/gamecontroller/presentations/Presentation";
import {WGParsedInitData} from "../../../server/data/WGParsedInitData";
import {ContinueButton} from "../ContinueButton";
import {Power1, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {StageEvent} from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import {LayoutDimData, ResizeHelper} from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import {WGParsedGameData} from "../../../server/data/WGParsedGameData";
import {WarriorGraveyardGameModuleConfig} from "../../../WarriorGraveyardGameModuleConfig";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {StateHandler} from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {WgShakingDisplayObject} from "../../../DisplayObject/WgShakingDisplayObject";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {SoundManager} from "../../../resource/sound/SoundManager";
import {WGGameConfig} from "../../../WGGameConfig";
import {WGSoundConfig} from "../../../WGSoundConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {WGStillImageBackground} from "../../../background/WGStillImageBackground";

export class IntroStates extends StateHandler {
    public static readonly HIDE_BACKGROUND_ELEMENTS: string = "hideBackgroundElements";
    public static readonly SHOW_FRAME: string = "showFrame";
    public static readonly SMASH_FREE_SPIN_COUNT: string = "smashFreeSpinCount";
    public static readonly SHOW_CONTINUE_BTN: string = "showContinueBtn";
    public static readonly DELAY: string = "delay";
}

export class IntroPresentation extends Presentation {
    private _introButton: ContinueButton;
    private _buttonShowed: boolean = false;
    private introPresentationSate: IntroStates = new IntroStates();
    private _introBackground: PIXI.Sprite;
    private _graveyardSpinsText: PIXI.Sprite;
    private _freeSpinNumber: PIXI.Sprite;
    private shakeFrameWhenTextPlaced: WgShakingDisplayObject;
    private _undergroundTransitionSprite: PIXI.Sprite;
    private _introFrame: PIXI.spine.Spine;
    private introNumberSmashSpine: PIXI.spine.Spine;
    private _freespinLeft: number = 0
    private _isSuperFreespin: boolean = false;

    /**
     * override this function to create and add button, other uI elements
     */
    public initialization(): void {
        // Background
        this._introBackground = new PIXI.Sprite(GameResources.getTextures("introBackground")[0]);
        this._introBackground.anchor.set(0.5);
        this._introBackground.position.y = -190;
        this._introBackground.visible = false;
        this._introBackground.alpha = 0;

        this.introNumberSmashSpine = this.addChild(new PIXI.spine.Spine(GameResources.getSpineAsset("intro_outro_spine")));
        this.introNumberSmashSpine.position.y = -210;
        this.introNumberSmashSpine.position.x = 5;
        this.introNumberSmashSpine.visible = false;

        //graveyard spins text
        this._graveyardSpinsText = new PIXI.Sprite(GameResources.getTextures("graveyardSpinsText")[0]);
        this._graveyardSpinsText.anchor.set(0.5);
        this._graveyardSpinsText.position.y = -100;
        this._graveyardSpinsText.position.x = 10;
        this._graveyardSpinsText.visible = false;

        // intro frame
        this._introFrame = this.addChild(new PIXI.spine.Spine(GameResources.getSpineAsset("intro_outro_spine")));
        this._introFrame.position.y = -200;
        this._introFrame.visible = false;

        // intro button
        this._introButton = new ContinueButton(() => {
            this.skipPresentation();
        });
        this._introButton.position.y += 100;
        this._introButton.position.x += 20;
        this._introButton.visible = false;

        this._freeSpinNumber = new PIXI.Sprite(GameResources.getTextures("freeSpinText_12")[0]);
        this._freeSpinNumber.anchor.set(0.5);
        this._freeSpinNumber.position.y = -250;
        this._freeSpinNumber.position.x = 10;
        this._freeSpinNumber.visible = false;

        this._undergroundTransitionSprite = new PIXI.Sprite(GameResources.getTextures("undergroundTransition")[0]);
        this._undergroundTransitionSprite.anchor.set(0.5);
        this._undergroundTransitionSprite.scale.set(2);
        this._undergroundTransitionSprite.visible = false;
        this.addChild(this._introBackground, this._introFrame, this.introNumberSmashSpine, this._introButton, this._freeSpinNumber, this._graveyardSpinsText, this._undergroundTransitionSprite);
    }

    /**
     * event to call resize function
     */
    public addEventListeners(): void {
        EventHandler.addEventListener(this, StageEvent.STAGE_RESIZED, () => {
            this.onResize();
        });
    }

    public checkForActivation(data: WGParsedInitData | WGParsedGameData): boolean {
        return data.mode === GameMode.NORMAL && data.nextMode === GameMode.FREESPIN;
    }

    /**
     * function to set timeline progress to 1 and calling stop from here.
     */
    public skipPresentation(): void {
        this.pTimeline.totalProgress(1);
        if (!this._isSuperFreespin) {
            SoundManager.playSoundEffect(WGSoundConfig.instance.FS_OUTRO_STONE_DOWN);
        }
        this._buttonShowed = false;
        this.introPresentationSate.set(IntroStates.DELAY);
    }

    public setParsedGamedData(data: WGParsedGameData): void {
        this._isSuperFreespin = data.superFreespin;
        this.introPresentationSate.set(IntroStates.HIDE_BACKGROUND_ELEMENTS);
        this._freespinLeft = data.freespinsLeft;
        WGGameConfig.CURRENT_GAME_MODE_BEFORE_SPIN_START = data.nextMode;
        this._freeSpinNumber.texture = (GameResources.getTextures(`freeSpinText_${data.freespinsLeft}`)[0]);
    }

    /**
     * to show intro presentation do nothing when buttonshowed
     */
    public getPresentation() {
        if (this._buttonShowed) {
            return;
        }
        switch (this.introPresentationSate.state) {
            case IntroStates.HIDE_BACKGROUND_ELEMENTS:
                SlotGame.keypad.hide();
                this._undergroundTransitionSprite.position.y = (this.position.y) + (this._undergroundTransitionSprite.height * 0.3);
                this.fadeInBackground();
                break;
            case IntroStates.SHOW_FRAME:
                this.introFrameAnimation();
                break;
            case IntroStates.SMASH_FREE_SPIN_COUNT:
                this.smashTextOnFrameAndShakeFrame();
                break;
            case IntroStates.SHOW_CONTINUE_BTN:
                this.shakeFrameWhenTextPlaced.stopShake();
                this._introButton.alpha = 0;
                this._introButton.visible = true;
                this.pTimeline.add(this.fadeInIntroButton());
                break;
            case IntroStates.DELAY:
                if (this._isSuperFreespin) {
                    WGStillImageBackground._isSuperFreespin = true;
                    this.introCloseAnimationForSuperFs();
                    this.pTimeline.to({}, 0.2, {});
                } else {
                    this.introCloseAnimation();
                }
                SoundManager.playFsLoop(this._isSuperFreespin);
                this.pTimeline.add(() => {
                    this.PresentationState.set(PresentationState.INACTIVE);
                });
                break;
        }
    }

    private introCloseAnimationForSuperFs(): void {
        const backgroundLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.BACKGROUND.name),
            reelbackgroundLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.REEL_BACKGROUND.name),
            reelLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.REEL.name),
            symbolsLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.SYMBOLS.name),
            symbolOverlayLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.SYMBOL_OVERLAY.name),
            reelOvelayLayer: ScalableLayer = StageManager.getLayer(WGGameConfig.instance.LAYERS.REEL_OVERLAY.name);

        this._introButton.visible = false;
        this._undergroundTransitionSprite.visible = true;
        WarriorGraveyardGameModuleConfig.stickMuliplier.showReelAreaMulitplier();
        WarriorGraveyardGameModuleConfig.freeSpinLeftMeter.resetMultipliers();
        let introFadeOut: TimelineMax = new TimelineMax({
            onComplete: () => {
                this.onResize();
                this._introFrame.visible = false;
                this.introNumberSmashSpine.visible = false;
                this._introBackground.visible = false;
                this._graveyardSpinsText.visible = false;
                this._freeSpinNumber.visible = false;
                this._undergroundTransitionSprite.visible = false;
            }
        });

        introFadeOut.add(() => {
            SoundManager.playSoundEffect(WGSoundConfig.instance.SUPER_FS_TRANSITION);
        });
        const duration: number = 1;
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {newMode: "FREESPIN"}));

        introFadeOut.fromTo(this, duration, {y: this.y}, {
            y: this.y - this.height,
            ease: Power1.easeInOut
        }, 0);
        let time = introFadeOut.time() + 0.5;
        introFadeOut.fromTo(backgroundLayer, duration, {y: backgroundLayer.y + 1000}, {y: backgroundLayer.y}, time);
        introFadeOut.fromTo(reelbackgroundLayer, duration, {y: reelbackgroundLayer.y + 1000}, {y: reelbackgroundLayer.y}, time + 0.2);
        introFadeOut.fromTo(reelLayer, duration, {y: reelLayer.y + 1000}, {y: reelLayer.y}, time + 0.2);
        introFadeOut.fromTo(symbolsLayer, duration, {y: symbolsLayer.y + 1000}, {y: symbolsLayer.y}, time + 0.2);
        introFadeOut.fromTo(symbolOverlayLayer, duration, {y: symbolOverlayLayer.y + 1000}, {y: symbolOverlayLayer.y}, time + 0.2);
        introFadeOut.fromTo(reelOvelayLayer, duration, {y: reelOvelayLayer.y + 1000}, {y: reelOvelayLayer.y}, time + 0.2);
        this.pTimeline.add(introFadeOut);
    }

    /**
     * function to fade in background
     * when presentation starts
     */
    private fadeInBackground(): void {
        SoundManager.playSoundEffect(WGSoundConfig.instance.FS_INTRO);
        if (this._isSuperFreespin) {
            this._introBackground.texture = GameResources.getTextures("outroBackground")[0];
        } else {
            this._introBackground.texture = GameResources.getTextures("introBackground")[0];
        }
        this._introBackground.alpha = 0;
        this._introBackground.visible = true;
        this.pTimeline.add(TweenLite.fromTo(this._introBackground, 1, {alpha: 0}, {alpha: 1}), 0.5);
        this.introPresentationSate.set(IntroStates.SHOW_FRAME);
    }

    /**
     * function to play animation on intro frame.
     */
    private introFrameAnimation(): void {
        this._introFrame.position.y += 1000;
        this.playIntroBottomToUpAnimationInSteps();
        this.pTimeline.to({}, 1, {}, 0.5);
        this.introPresentationSate.set(IntroStates.SMASH_FREE_SPIN_COUNT);
    }

    /**
     * function to introduce intro frame from bottom to top
     */
    private playIntroBottomToUpAnimationInSteps(): void {
        const posY = this._introFrame.position.y;
        const animName = this._isSuperFreespin ? "outro_start" : "intro_start";
        this.pTimeline.add(
            () => {
                TweenLite.fromTo(this._introFrame, 1, {y: posY}, {
                    y: posY - 1000,
                });
                new SpineTween(this._introFrame, 0, animName);
            });
        // to fix #146
        this.pTimeline.add(() => {
            this._introFrame.updateTransform();
            this._introFrame.visible = true;
        }, 0.01);
    }

    /**
     * function to play with smashing animation
     */
    private smashTextOnFrameAndShakeFrame(): void {
        const textureName = this._freespinLeft > 13 ? "deathSpinsText" : "graveyardSpinsText";
        this._graveyardSpinsText.texture = GameResources.getTextures(textureName)[0];
        const scale: number = this._freespinLeft > 8 ? 0.8 : 1;

        this.shakeFrameWhenTextPlaced = new WgShakingDisplayObject(this._introFrame);
        this.introNumberSmashSpine.updateTransform();
        this.introNumberSmashSpine.visible = true;
        const fsBlastType = this._isSuperFreespin ? "outro_fs_awarded" : "intro_fs_awarded";
        new SpineTween(this.introNumberSmashSpine, 0, fsBlastType);
        this._freeSpinNumber.visible = true;
        this.pTimeline.add(TweenLite.fromTo(this._freeSpinNumber.scale, 0.2, {x: 10, y: 10},
            {x: scale, y: scale}));
        this._graveyardSpinsText.visible = true;
        this.pTimeline.add(TweenLite.fromTo(this._graveyardSpinsText.scale, 0.2, {x: 10, y: 10},
            {x: 1, y: 1}), 0);
        this.pTimeline.add(() => {
            this.shakeFrameWhenTextPlaced.textPlacedAnimation(0.05, -0.05, -0.05);
        }, 0.25);
        this.pTimeline.add(() => {
            const loopAnimType = this._isSuperFreespin ? "outro_restore" : "intro_loop";
            new SpineTween(this._introFrame, 0, loopAnimType, -1);
        });

        SoundManager.playSoundEffect(WGSoundConfig.instance.FS_INTRO_EXPLODE);
        this.introPresentationSate.set(IntroStates.SHOW_CONTINUE_BTN);
    }

    /**
     * function to fade in intro button
     * added 100000000000 in duration to avoid getPresentation call in loop.
     */
    private fadeInIntroButton(): TimelineLite {
        this._buttonShowed = true;
        let tl: TimelineLite = new TimelineLite();
        tl.add(TweenLite.fromTo(this._introButton, 0.5, {alpha: 0}, {alpha: 1}));
        tl.add(TweenLite.to(this._introButton, 100000000000, {}));
        return tl;
    }

    private introCloseAnimation(): void {
        WarriorGraveyardGameModuleConfig.stickMuliplier.showReelAreaMulitplier();
        WarriorGraveyardGameModuleConfig.freeSpinLeftMeter.resetMultipliers();
        this._introButton.visible = false;
        let introFadeOut: TimelineMax = new TimelineMax({
            onComplete: () => {
                this._introFrame.visible = false;
                this._introFrame.position.y -= 1000;
                this.introNumberSmashSpine.visible = false;
                this.introNumberSmashSpine.y = -210;
                this.introNumberSmashSpine.skeleton.setToSetupPose();
                this.introNumberSmashSpine.state.tracks = [];
                this._introFrame.skeleton.setToSetupPose();
                this._introFrame.state.tracks = [];
                this._introBackground.visible = false;
                this._graveyardSpinsText.visible = false;
                this._graveyardSpinsText.position.y -= 1000;
                this._freeSpinNumber.visible = false;
                this._freeSpinNumber.position.y -= 1000;

                // This will turn off Autoplay if in restricted jurisdiction
                if(SlotGame.autoPlay.turnOffAutoplayOnBonus) {
                    SlotGame.autoPlay.turnOffAutoplayOnBonus();
                }
            }
        });
        EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {newMode: "FREESPIN"}));
        WarriorGraveyardGameModuleConfig.background.startFSBgRaining();
        introFadeOut.add(TweenLite.fromTo(this._introFrame, 1, {y: this._introFrame.position.y}, {y: this._introFrame.position.y + 1000}), 0);
        introFadeOut.add(TweenLite.fromTo(this.introNumberSmashSpine, 1, {y: this.introNumberSmashSpine.position.y}, {y: this.introNumberSmashSpine.position.y + 1000}), 0);
        introFadeOut.add(TweenLite.fromTo(this._freeSpinNumber, 1, {y: this._freeSpinNumber.position.y}, {y: this._freeSpinNumber.position.y + 1000}), 0);
        introFadeOut.add(TweenLite.fromTo(this._graveyardSpinsText, 1, {y: this._graveyardSpinsText.position.y}, {y: this._graveyardSpinsText.position.y + 1000}), 0);
        introFadeOut.add(TweenLite.fromTo(this._introBackground, 1, {alpha: 1}, {alpha: 0}), 0.2);
        this.pTimeline.add(introFadeOut);
    }

    /**
     * function to resize this.
     */
    public onResize(): void {
        const dimConf = ContinueButton.continueDim;
        let layoutdata: LayoutDimData = ResizeHelper.calcDim(dimConf);
        this.position = layoutdata.position;
        this.scale = layoutdata.scale;
    }
}